import { supabase } from "@/lib/supabase";

export interface MyMeetupEventRow {
  participant_id: string;
  event_id: string;
  slug: string | null;
  title: string;
  event_date: string;
  location: string | null;
  status: "draft" | "active" | "completed" | "cancelled";
  match_reveal_hours: number;
  pitch_status: "pending" | "submitted" | "approved" | "flagged" | "rejected";
  has_video: boolean;
  video_storage_path: string | null;
  matches_count: number;
  organizer_id: string;
  organizer_name: string;
  registered_at: string;
}

/**
 * Account Home / "My events". Lists every meetup event the same email has
 * registered for. Authorized by knowing one of the user's participant IDs.
 *
 * @param participantId — any participantId previously stored on this device.
 *  Backed by the `find_my_meetup_events` RPC (security definer).
 */
export async function fetchMyMeetupEvents(
  participantId: string,
): Promise<MyMeetupEventRow[]> {
  if (!participantId) return [];
  const { data, error } = await supabase.rpc("find_my_meetup_events", {
    p_participant_id: participantId,
  });
  if (error) throw new Error(error.message);
  const raw = (data ?? []) as Array<Record<string, unknown>>;
  return raw.map((r) => ({
    participant_id: r.participant_id as string,
    event_id: r.event_id as string,
    slug: (r.slug as string | null) ?? null,
    title: r.title as string,
    event_date: r.event_date as string,
    location: (r.location as string | null) ?? null,
    status: r.status as MyMeetupEventRow["status"],
    match_reveal_hours: (r.match_reveal_hours as number) ?? 2,
    pitch_status: r.pitch_status as MyMeetupEventRow["pitch_status"],
    has_video: Boolean(r.has_video),
    video_storage_path: (r.video_storage_path as string | null) ?? null,
    matches_count: Number(r.matches_count ?? 0),
    organizer_id: r.organizer_id as string,
    organizer_name: (r.organizer_name as string) ?? "Organizer",
    registered_at: r.registered_at as string,
  }));
}

/* ----- categorization helpers used by Account Home ----- */

export type EventBucket = "live" | "upcoming" | "past" | "cancelled";

export function bucketEvent(event: MyMeetupEventRow, now = new Date()): EventBucket {
  if (event.status === "cancelled") return "cancelled";
  const eventTime = new Date(event.event_date).getTime();
  const nowTime = now.getTime();
  // Live: anywhere from reveal time through 2h after event start.
  const revealAt = eventTime - event.match_reveal_hours * 3_600_000;
  const eventEnd = eventTime + 2 * 3_600_000;
  if (event.status === "active" && nowTime >= revealAt && nowTime <= eventEnd) {
    return "live";
  }
  if (eventTime > nowTime && event.status !== "completed") return "upcoming";
  return "past";
}

/** Auto-delete date for past events (30 days after the event). */
export function autoDeleteAt(event: MyMeetupEventRow): Date {
  return new Date(new Date(event.event_date).getTime() + 30 * 86_400_000);
}
